import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Typography } from "@mui/material";
import { toast } from "react-toastify";
import moment from "moment";

import { ActionButton, CardLayout, InputField, SelectField, ToggleButton } from "../v3";
import { CRMService, ProjectService } from "../../services";
import { projectValidationRules } from "../../pages/project/validation";
import { AddEditClient } from "../client";
import { PROJECT_TYPES, validateDateRange } from "../../utils";
import { markTableReset } from "../../utils/tableStateStorage";

export const AddEditProjectForm = ({ projectId }) => {
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [openClientModal, setOpenClientModal] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedProjectType, setSelectedProjectType] = useState(null);
  const [projectDetails, setProjectDetails] = useState({
    name: undefined,
    description: undefined,
    status: "INPROGRESS",
    type: "CONSTRUCTION",
    isGovernment: false,
    clientId: undefined,
    estimatedAmount: undefined,
    expectedStartDate: undefined,
    expectedEndDate: undefined,
    tender: {
      id: undefined,
      agreementDate: undefined,
      siteHandoverDate: undefined,
      agreementDuration: undefined,
      agreementEndDate: undefined,
      notes: undefined,
    },
    dlp: {
      duration: undefined,
      notes: undefined,
    },
  });

  const navigate = useNavigate();

  useEffect(() => {
    if (projectId) fetchProjectDetails(projectId);
    else handleClearForm();
  }, [projectId]);

  const fetchProjectDetails = async (id) => {
    try {
      const response = await ProjectService.getProjectById(id);
      if (projectId && response?.data?.clientId) {
        const client = response?.data?.client || null;
        if (client) {
          setSelectedClient(client);
        }
      }

      setProjectDetails({
        name: response?.data?.name || null,
        clientId: response?.data?.clientId || null,
        type: response?.data?.type || null,
        expectedStartDate: response?.data?.expectedStartDate,
        expectedEndDate: response?.data?.expectedEndDate,
        estimatedAmount: Number(response?.data?.estimatedAmount) || null,
        description: response?.data?.description || null,
        isGovernment: response?.data?.isGovernment,
        tender: {
          id: response?.data?.tender?.id || null,
          agreementDate: response?.data?.tender?.agreementDate,
          siteHandoverDate: response?.data?.tender?.siteHandoverDate,
          agreementDuration: response?.data?.tender?.agreementDuration || null,
          agreementEndDate: response?.data?.tender?.agreementEndDate,
          notes: response?.data?.tender?.notes || null,
        },
        dlp: {
          duration: response?.data?.dlp?.duration || null,
          notes: response?.data?.dlp?.notes || null,
        },
      });
      if (response?.data?.type) {
        const selectedType = PROJECT_TYPES.find((type) => type.value === response.data.type);
        setSelectedProjectType(selectedType || null);
      }
    } catch (error) {
      toast("Failed to fetch project details", { type: "error" });
      console.error("🚀 ~ fetchProjectDetails ~ error:", error);
    }
  };

  const handleClientChange = (selected) => {
    setSelectedClient(selected);
    setProjectDetails((prev) => ({ ...prev, clientId: selected?.id || null }));
  };

  const handleTypeChange = (selected) => {
    setSelectedProjectType(selected);
    setProjectDetails((prev) => ({ ...prev, type: selected?.value || null }));
  };

  const getProjectTypeOptions = () =>
    Promise.resolve({
      data: PROJECT_TYPES,
    });

  const handleClearForm = () => {
    setProjectDetails({
      name: null,
      description: null,
      status: "INPROGRESS",
      type: "CONSTRUCTION",
      isGovernment: false,
      clientId: null,
      estimatedAmount: null,
      expectedStartDate: null,
      expectedEndDate: null,
      tender: {
        id: null,
        agreementDate: null,
        siteHandoverDate: null,
        agreementDuration: null,
        agreementEndDate: null,
        notes: null,
      },
      dlp: {
        duration: null,
        notes: null,
      },
    });
    setSelectedClient(null);
    setErrors({});
    const defaultConstructionType = PROJECT_TYPES.find((type) => type.value === "CONSTRUCTION") || null;
    setSelectedProjectType(defaultConstructionType);
  };

  const updateDetailsState = (event) => {
    const { name, value } = event.target;

    if (name === "type") {
      const sanitized = value.trim().replace(/\s+/g, "").toUpperCase();
      setProjectDetails((prev) => ({ ...prev, [name]: sanitized }));
    }

    // handle project-level dates
    else if (name === "expectedStartDate" || name === "expectedEndDate") {
      setProjectDetails((prev) => ({
        ...prev,
        [name]: value,
      }));

      const updated = {
        ...projectDetails,
        [name]: value,
      };

      const dateErrors = validateDateRange({
        fieldName: name,
        startDateKey: "expectedStartDate",
        endDateKey: "expectedEndDate",
        startDate: updated.expectedStartDate,
        endDate: updated.expectedEndDate,
      });

      setErrors((prev) => ({
        ...prev,
        expectedStartDate: dateErrors.expectedStartDate,
        expectedEndDate: dateErrors.expectedEndDate,
      }));
    }

    // handle estimated amount validation
    else if (name === "estimatedAmount") {
      setProjectDetails((prev) => ({ ...prev, estimatedAmount: value }));

      const num = parseFloat(value);
      if (value !== "" && (isNaN(num) || num <= 0)) {
        setErrors((prev) => ({ ...prev, estimatedAmount: "must be greater than 0" }));
      } else {
        setErrors((prev) => ({ ...prev, estimatedAmount: null }));
      }
    }

    // handle tender-level dates
    else if (name.startsWith("tender.")) {
      const tenderName = name.split(".")[1];

      setProjectDetails((prevDetails) => {
        const updatedTender = { ...prevDetails.tender, [tenderName]: value };

        if (["agreementDate", "siteHandoverDate"].includes(tenderName)) {
          const tenderDateErrors = validateDateRange({
            fieldName: tenderName,
            startDateKey: "agreementDate",
            endDateKey: "siteHandoverDate",
            startDate: updatedTender.agreementDate,
            endDate: updatedTender.siteHandoverDate,
            startErrorText: "Agreement date cannot be after site handover date",
            endErrorText: "Site handover date cannot be before agreement date",
          });

          setErrors((prev) => ({
            ...prev,
            tender: {
              ...prev.tender,
              agreementDate: tenderDateErrors.agreementDate,
              siteHandoverDate: tenderDateErrors.siteHandoverDate,
            },
          }));
        }

        // auto-calculate agreement end date
        if (tenderName === "siteHandoverDate" || tenderName === "agreementDuration") {
          if (updatedTender.siteHandoverDate && updatedTender.agreementDuration) {
            const endDate = moment(updatedTender.siteHandoverDate)
              .add(updatedTender.agreementDuration, "months")
              .format("YYYY-MM-DD");
            updatedTender.agreementEndDate = endDate;
          }
        }

        return { ...prevDetails, tender: updatedTender };
      });
    }

    //  handle nested DLP fields
    else if (name.startsWith("dlp.")) {
      const dlpName = name.split(".")[1];
      setProjectDetails((prevDetails) => ({
        ...prevDetails,
        dlp: { ...prevDetails.dlp, [dlpName]: value },
      }));
    }

    // handle all other fields
    else {
      setProjectDetails((prevDetails) => ({ ...prevDetails, [name]: value }));
    }
  };

  const handleSwitchChange = (name) => {
    setProjectDetails((prev) => ({
      ...prev,
      [name]: !prev[name],
    }));
  };

  const handleCancelClick = () => {
    handleClearForm();
    navigate(-1);
  };

  const formatProjectPayload = (details, isEdit) => {
    const projectData = { ...details };

    Object.keys(projectData).forEach((key) => {
      if (typeof projectData[key] === "string") {
        projectData[key] = projectData[key].trim();
        if (projectData[key] === "") {
          if (isEdit) projectData[key] = null;
          else delete projectData[key];
        }
      }
    });

    if (typeof projectData.dlp?.notes === "string") {
      let value = projectData.dlp.notes.trim();
      if (value === "") {
        projectData.dlp.notes = isEdit ? null : undefined;
        if (!isEdit) delete projectData.dlp.notes;
      } else {
        projectData.dlp.notes = value;
      }
    }

    return projectData;
  };

  const handleSaveProject = async () => {
    if (loading) return;
    setErrors({});

    const formattedProjectDetails = formatProjectPayload(projectDetails, !!projectId);
    const { estimatedAmount, ...rest } = formattedProjectDetails;
    const payload = {
      ...rest,
      ...(estimatedAmount ? { estimatedAmount: Number(estimatedAmount) } : {}),
    };

    const validationErrors = projectValidationRules(payload);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    try {
      setLoading(true);
      if (projectId) await ProjectService.updateProject(projectId, payload);
      else await ProjectService.createProject(payload);

      toast.success(`Project ${projectId ? "updated" : "created"}  successfully`);
      // A new record would be hidden by the filters the list had, so it opens fresh; after an edit they're kept.
      if (!projectId) markTableReset();
      handleCancelClick();
    } catch (error) {
      let errorMessage = `Error ${projectId ? "updating" : "adding"} project`;
      const response = error?.response?.data;

      if (Array.isArray(response?.message)) {
        errorMessage = response.message.join(", ");
      } else if (typeof response?.message === "string") {
        errorMessage = response.message;
      }

      toast(errorMessage, { type: "error" });
      console.error("🚀 ~ handleSaveProject ~ error:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <CardLayout>
        <Box className="fl gap-2">
          <InputField
            required
            label="Project Name"
            name="name"
            value={projectDetails.name}
            onChange={updateDetailsState}
            error={!!errors.name}
            helperText={errors.name}
            fullWidth
          />
          <SelectField
            label="Client"
            value={selectedClient}
            getData={CRMService.getClientList}
            getDataStaticFilters={{ active: true }}
            required
            error={!!errors.clientId}
            helperText={errors.clientId}
            onChange={handleClientChange}
            addButtonText="Add Client"
            showAddButton={true}
            onAddClick={() => {
              setOpenClientModal(true);
            }}
            style={{ width: "100%" }}
          />
          <SelectField
            label="Project Type"
            value={selectedProjectType}
            getData={getProjectTypeOptions}
            required
            error={!!errors.type}
            helperText={errors.type}
            onChange={handleTypeChange}
            style={{ width: "100%" }}
          />
        </Box>

        <Box className="fl gap-2">
          <InputField
            required
            type="date"
            label="Expected Start Date"
            name="expectedStartDate"
            value={projectDetails?.expectedStartDate}
            onChange={updateDetailsState}
            error={!!errors.expectedStartDate}
            helperText={errors.expectedStartDate}
            fullWidth
          />
          <InputField
            required
            type="date"
            label="Expected End Date"
            name="expectedEndDate"
            value={projectDetails?.expectedEndDate}
            onChange={updateDetailsState}
            error={!!errors.expectedEndDate}
            helperText={errors.expectedEndDate}
            fullWidth
          />
          <InputField
            type="number"
            label="Estimated Amount"
            name="estimatedAmount"
            value={projectDetails?.estimatedAmount}
            onChange={updateDetailsState}
            error={!!errors.estimatedAmount}
            helperText={errors.estimatedAmount}
            fullWidth
          />
        </Box>
        <Box className="fl gap-2">
          <InputField
            label="Description"
            name="description"
            value={projectDetails.description}
            onChange={updateDetailsState}
            error={!!errors.description}
            helperText={errors.description}
            fullWidth
            autoComplete="on"
          />
        </Box>

        <Box display="flex" alignItems="center" gap={2}>
          <ToggleButton
            label="Government Project"
            checked={projectDetails.isGovernment}
            onToggleChange={() => handleSwitchChange("isGovernment")}
          />
        </Box>

        {/* Conditionally Show Tender & DLP Fields */}
        {projectDetails.isGovernment && (
          <>
            <Typography variant="body2" sx={{ mt: 2, fontWeight: "bold" }}>
              Tender
            </Typography>
            <Box className="fl gap-2" mt={3}>
              <InputField
                required
                type="date"
                label="Agreement Date"
                name="tender.agreementDate"
                value={projectDetails.tender.agreementDate}
                onChange={updateDetailsState}
                error={!!errors.tender?.agreementDate}
                helperText={errors.tender?.agreementDate}
                fullWidth
              />
              <InputField
                required
                type="date"
                label="Site Handover Date"
                name="tender.siteHandoverDate"
                value={projectDetails.tender.siteHandoverDate}
                onChange={updateDetailsState}
                error={!!errors.tender?.siteHandoverDate}
                helperText={errors.tender?.siteHandoverDate}
                fullWidth
              />
            </Box>
            <Box className="fl gap-2">
              <InputField
                required
                type="number"
                label="Agreement Duration (Months)"
                name="tender.agreementDuration"
                value={projectDetails.tender.agreementDuration}
                onChange={updateDetailsState}
                error={!!errors.tender?.agreementDuration}
                helperText={errors.tender?.agreementDuration}
                fullWidth
              />
              <InputField
                required
                label="Agreement End Date"
                name="tender.agreementEndDate"
                disabled={true}
                type="date"
                value={projectDetails.tender.agreementEndDate}
                onChange={updateDetailsState}
                error={!!errors.tender?.agreementEndDate}
                helperText={errors.tender?.agreementEndDate}
                fullWidth
              />
            </Box>

            <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
              DLP
            </Typography>

            <Box className="fl gap-2" mt={2}>
              <InputField
                type="number"
                label="DLP Duration (Years)"
                name="dlp.duration"
                value={projectDetails.dlp.duration}
                onChange={updateDetailsState}
                fullWidth
              />
              <InputField
                label="DLP Notes"
                name="dlp.notes"
                value={projectDetails.dlp.notes}
                onChange={updateDetailsState}
                fullWidth
              />
            </Box>
          </>
        )}
        <Box className={"d-flex justify-content-end gap-1"}>
          <ActionButton buttonText="cancel" variant="outlined" onButtonClick={handleCancelClick} />
          <ActionButton buttonText="Submit" color="primary" onButtonClick={handleSaveProject} loading={loading} />
        </Box>
      </CardLayout>

      {/* Client Modal */}
      <AddEditClient
        open={openClientModal}
        onClose={() => setOpenClientModal(false)}
        onSuccess={() => {
          setOpenClientModal(false);
        }}
      />
    </>
  );
};
